import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Icon } from '@/components/Icon';
import { CancelFlow } from '@/features/billing/CancelFlow';
import { TopUpSheet } from '@/features/billing/TopUpSheet';
import { fetchAccount } from '@/lib/api';
import { formatPrice } from '@/lib/pricing';
import type { MeterUsage } from '@/lib/billing';
import { TIERS } from '@/lib/tier';
import { SignOutButton } from '@/features/auth/SignOutButton';
import './Account.css';

/**
 * Account & Billing.
 *
 * Naming rules that are not preferences:
 *   - The button is **"Manage Plans"**, never the billing vendor's name. Neither Kinde nor
 *     Paddle is ever named in customer-facing UI.
 *   - **"Notifications"**, not "Alerts" — alerts are a setting on a Signal, not a page.
 *   - **"questions"** for Ask Aware, **"uses"** for Aware Explain. Never "tokens", never
 *     "credits" as a generic term.
 *
 * Cancellation is self-serve and lives here as a quiet text link, not a competing button — but
 * it is never hidden and never gated behind a support conversation.
 *
 * Throughout a trial, the exact charge date and amount are shown. "14-day free trial" alone is
 * not sufficient disclosure when a card is on file.
 */
export function Account() {
  const [canceling, setCanceling] = useState(false);
  const [toppingUp, setToppingUp] = useState(false);

  const { data: account, isLoading } = useQuery({ queryKey: ['account'], queryFn: fetchAccount });

  if (isLoading || !account) return <p className="account__empty">Loading…</p>;

  // No subscription on record. The API answers this route for such a user on purpose, so this
  // screen must render something rather than crash on a null tier — they are sent to the plan
  // chooser by the shell, and this is the moment before that redirect lands.
  if (account.tier === null) {
    return <p className="account__empty">No active plan. Taking you to plan options…</p>;
  }

  const plan = TIERS[account.tier];
  const price = (n: number) => formatPrice(account.currency, n);
  const per = account.billingPeriod === 'monthly' ? '/month' : '/year';
  const amount = account.nextChargeAmount;

  return (
    <div className="account">
      <header className="account__head">
        <h1 className="account__title">Account &amp; Billing</h1>
      </header>

      <div className="account__grid">
        {/* ---- plan ---- */}
        <section className="account__card">
          <h2 className="u-meta">Your plan</h2>
          <p className="account__plan">{plan.label}</p>
          <p className="account__price">
            {amount === null ? '—' : `${price(amount)}${per}`}
          </p>

          {account.inTrial ? (
            /* The exact date and amount, everywhere the trial is mentioned. */
            <p className="account__trial">
              <Icon name="check" /> Free until <strong>{account.trialEndsOn}</strong>
              {amount !== null && (
                <>
                  , then{' '}
                  <strong>
                    {price(amount)}
                    {per}
                  </strong>
                </>
              )}
              . Cancel any time before then and you won’t be charged.
            </p>
          ) : account.accessEndsOn ? (
            <p className="account__ending">
              Canceled. You keep full access until <strong>{account.accessEndsOn}</strong>.
            </p>
          ) : (
            <p className="account__next">
              <Icon name="calendar" /> Next charge {account.nextChargeDate}
            </p>
          )}

          <div className="account__actions">
            {/* "Manage Plans" — never the vendor's name. */}
            <Link to="/pricing" className="account__primary">
              Manage Plans
            </Link>
          </div>

          {!account.accessEndsOn && (
            <button type="button" className="account__cancelLink" onClick={() => setCanceling(true)}>
              Cancel plan
            </button>
          )}
        </section>

        {/* ---- usage ---- */}
        <section className="account__card">
          <h2 className="u-meta">Usage this period</h2>

          <Meter label="Aware Explain" unit="uses" usage={account.awareExplain} />
          {/* Ask Aware only exists on Intelligence Pro, so the meter only appears there. */}
          {account.askAware && (
            <Meter label="Ask Aware" unit="questions" usage={account.askAware} />
          )}

          <button type="button" className="account__topUp" onClick={() => setToppingUp(true)}>
            <Icon name="plus" /> Add a top-up pack
          </button>
          <p className="account__topUpNote">
            Top-up packs are a one-time purchase and never expire. They stack on top of your
            monthly allowance and carry over if you change plan.
          </p>
        </section>

        {/* ---- notifications ---- */}
        <section className="account__card">
          {/* "Notifications", not "Alerts" — alerts are a setting on a Signal. */}
          <h2 className="u-meta">Notifications</h2>
          <ul className="account__channels">
            <li>
              <span className="account__channelName">Email</span>
              <span className="account__channelValue">{account.emailAddress}</span>
              <span className="account__channelState" data-on="true">
                On
              </span>
            </li>
            <li>
              <span className="account__channelName">WhatsApp</span>
              <span className="account__channelValue">
                {account.whatsAppNumber ?? 'Not connected'}
              </span>
              {account.whatsAppNumber ? (
                <span className="account__channelState" data-on="true">
                  On
                </span>
              ) : (
                <button type="button" className="account__connect">
                  Connect
                </button>
              )}
            </li>
          </ul>
          <p className="account__channelNote">
            Delivery frequency is set per signal and per followed town, not here — each one can
            run at a different cadence.
          </p>
        </section>
      </div>

      {/* Sign out lives at the foot of Account because that is where people look for it, and
          because on desktop there is no More screen to carry it. Below the plan and the meters:
          it is an exit, not a setting. */}
      <div className="account__signout">
        <SignOutButton variant="button" />
      </div>

      {canceling && (
        <CancelFlow
          account={account}
          onClose={() => setCanceling(false)}
          onCancelConfirmed={() => setCanceling(false)}
        />
      )}

      {toppingUp && (
        <TopUpSheet
          currency={account.currency}
          hasAskAware={account.askAware !== null}
          onClose={() => setToppingUp(false)}
        />
      )}
    </div>
  );
}

/**
 * A usage meter.
 *
 * The monthly allowance and any purchased balance are shown as two distinct figures, because
 * they behave differently: the allowance resets each period, the paid balance never expires
 * and carries over on a plan change. Presenting them as one number would make the carry-over
 * rule impossible to explain.
 */
function Meter({ label, unit, usage }: { label: string; unit: string; usage: MeterUsage }) {
  const pct = usage.allowance ? Math.min(100, Math.round((usage.used / usage.allowance) * 100)) : 0;

  return (
    <div className="account__meter">
      <div className="account__meterHead">
        <span className="account__meterLabel">{label}</span>
        <span className="account__meterCount">
          {usage.used.toLocaleString('en-US')}
          {usage.allowance !== null && ` of ${usage.allowance.toLocaleString('en-US')}`} {unit}
        </span>
      </div>
      <div className="account__meterTrack">
        <span className="account__meterFill" style={{ width: `${pct}%` }} />
      </div>
      {usage.paidBalance !== null && (
        <p className="account__meterPaid">
          + {usage.paidBalance.toLocaleString('en-US')} {unit} purchased — never expire
        </p>
      )}
    </div>
  );
}
